import React, { useEffect } from "react";
import { Redirect, useParams } from "react-router-dom";
import styled from "styled-components";
import { getCategoryCounts, getNewsletters } from "../../content/newsletters";
import {
  categoryAccent,
  categoryDescription,
  categoryLabel,
  getCategory,
} from "../../content/newsletters/categories";
import CategoryNav from "../../components/Newsletter/CategoryNav";
import PostLink from "../../components/Newsletter/PostLink";
import SubscribeForm from "../../components/Newsletter/SubscribeForm";
import { Page, Wrap } from "../../components/Newsletter/theme";

const TEXT = {
  ko: {
    title: "뉴스레터",
    empty: "아직 발행된 뉴스레터가 없습니다.",
    emptyNote:
      "첫 호를 준비하고 있습니다. 아래에 이메일이나 전화번호를 남겨두시면 나오는 대로 알려드릴게요.",
    emptyCategory: (name) => `아직 ${name} 소식이 없습니다.`,
    emptyCategoryNote:
      "첫 소식을 준비하고 있습니다. 아래에 이메일이나 전화번호를 남겨두시면 나오는 대로 알려드릴게요.",
    documentTitle: "뉴스레터 | 바삭한소프트",
    categoryDocumentTitle: (name) => `${name} · 뉴스레터 | 바삭한소프트`,
  },
  en: {
    title: "Newsletter",
    empty: "No issues published yet.",
    emptyNote:
      "The first one is on its way. Leave an email or a phone number below and we'll let you know.",
    emptyCategory: (name) => `No ${name} posts yet.`,
    emptyCategoryNote:
      "The first one is on its way. Leave an email or a phone number below and we'll let you know.",
    documentTitle: "Newsletter | Basakansoft",
    categoryDocumentTitle: (name) => `${name} · Newsletter | Basakansoft`,
  },
};

function NewsletterList() {
  const { lang, category: categoryId } = useParams();
  const language = lang === "en" ? "en" : "ko";
  const t = TEXT[language];
  const category = categoryId ? getCategory(categoryId) : null;
  const label = category ? categoryLabel(category, language) : null;
  const documentTitle = label ? t.categoryDocumentTitle(label) : t.documentTitle;

  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  // 없는 카테고리 주소는 전체 목록으로 돌려보낸다.
  if (categoryId && !category) {
    return <Redirect to={`/${language}/newsletter`} />;
  }

  const posts = getNewsletters(language, category ? category.id : null);
  const counts = getCategoryCounts(language);
  const total = getNewsletters(language).length;

  // 게임 카테고리를 보고 있으면 지면 강조색(링크 hover, 포커스 테두리)도 그 게임 색으로.
  const pageStyle = category ? { "--nl-accent": categoryAccent(category) } : undefined;

  return (
    <Page style={pageStyle}>
      <Wrap>
        <Header>
          <Title>{t.title}</Title>
        </Header>

        {total > 0 || category ? (
          <CategoryNav
            lang={language}
            active={category ? category.id : null}
            counts={counts}
            total={total}
          />
        ) : null}

        {category ? (
          <Intro>
            {/* 로고는 한글 표기라 영문 화면에서는 이름을 글자로 쓴다. */}
            {language === "ko" && category.game && category.game.logo ? (
              <IntroTitle>
                <IntroLogo src={category.game.logo.src} alt={label} />
              </IntroTitle>
            ) : (
              <IntroTitle>{label}</IntroTitle>
            )}
            <IntroText>{categoryDescription(category, language)}</IntroText>
          </Intro>
        ) : null}

        {posts.length > 0 ? (
          <List>
            {posts.map((post) => (
              <PostLink
                key={post.slug}
                post={post}
                lang={language}
                showCategory={!category}
              />
            ))}
          </List>
        ) : (
          <Empty>
            <EmptyTitle>{label ? t.emptyCategory(label) : t.empty}</EmptyTitle>
            <EmptyNote>{label ? t.emptyCategoryNote : t.emptyNote}</EmptyNote>
          </Empty>
        )}

        <SubscribeForm
          lang={language}
          source={category ? `newsletter-list:${category.id}` : "newsletter-list"}
        />
      </Wrap>
    </Page>
  );
}

const Header = styled.header`
  padding: 5rem 0 1.5rem;

  @media screen and (max-width: 768px) {
    padding: 3rem 0 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.035em;
  color: var(--nl-ink);

  @media screen and (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const Intro = styled.div`
  padding: 2.25rem 0 0;
`;

const IntroTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: -0.03em;
  color: var(--nl-ink);
`;

/* 게임 로고는 지면 안에서는 작게. 가로는 비율대로, 좁은 화면에서는 폭에 맞춰 줄어든다. */
const IntroLogo = styled.img`
  display: block;
  height: 72px;
  max-width: 100%;
  width: auto;
  object-fit: contain;
  object-position: left center;
`;

const IntroText = styled.p`
  max-width: 30rem;
  margin-top: 0.625rem;
  font-size: 0.9375rem;
  line-height: 1.75;
  color: var(--nl-ink-soft);
`;

const List = styled.div`
  padding: 2.25rem 0 1rem;
`;

const Empty = styled.div`
  margin-top: 2.25rem;
  padding: 3rem 0 3.5rem;
  border-top: 1px solid var(--nl-line);
`;

const EmptyTitle = styled.p`
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--nl-ink);
`;

const EmptyNote = styled.p`
  max-width: 28rem;
  margin-top: 0.5rem;
  font-size: 0.9375rem;
  line-height: 1.75;
  color: var(--nl-ink-soft);
`;

export default NewsletterList;
